import { mixColors } from '@avrahami/contracts';
import { flyerAddress, textColorOn } from '../layout';
import {
  benefitIcon,
  bodyFont,
  cornerOf,
  displayFont,
  drawAmount,
  drawIcon,
  drawLines,
  drawLogo,
  drawPhoto,
  drawStruck,
  drawText,
  fillRect,
  fit,
  fitDescription,
  line,
  phoneOf,
  PHONE_ICON,
  readable,
  strokeRect,
  W,
  WHATSAPP_ICON,
  type Page,
} from '../kit';
import { clipBody, kickerOf, savingOf, tilesOf, WORDS } from './shared';

/**
 * PLANNER: light and solution-led. A white header with both logos, a warm grey opening with the
 * title, the description and the maker beside the photo, four white cards of figures, a price
 * bar in the brand's colour with a white button, and a dark footer with the phone.
 */

const GREY = '#f4f2f0';
const RIGHT = 1016;
const LEFT = 64;
const WHITE = '#ffffff';

export function drawPlanner(page: Page) {
  const { ctx, flyer } = page;
  const { theme, storeDesign } = flyer;
  const ink = storeDesign.ink;
  const muted = mixColors(ink, GREY, 0.4);
  const brand = readable(theme.brand, WHITE, [theme.brandDark, ink], 4.5);
  const radius = cornerOf(storeDesign.corners, 22);
  const tiles = tilesOf(flyer, 4);
  const heroBottom = tiles.length === 0 ? 770 : 632;

  fillRect(ctx, { x: 0, y: 0, w: W, h: W }, WHITE);

  // The header: the store's logo, and the maker's when the page shows one.
  drawLogo(
    page,
    page.images.logo,
    flyer.store.name,
    { x: 560, y: 34, w: RIGHT - 560, h: 66 },
    {
      align: 'right',
      color: ink,
    },
  );
  drawLogo(
    page,
    page.images.maker,
    null,
    { x: LEFT, y: 42, w: 210, h: 52 },
    { align: 'left', color: ink },
  );

  // The opening: the photo on warm grey beside the words.
  fillRect(ctx, { x: 0, y: 132, w: W, h: heroBottom - 132 }, GREY);
  drawPhoto(
    page,
    { x: LEFT, y: 172, w: 470, h: heroBottom - 172 - (heroBottom === 632 ? 80 : 60) },
    radius,
  );

  const width = 440;
  let y = 172;
  const saving = savingOf(flyer);
  const tag = saving !== null ? WORDS.sale : flyer.category;
  if (tag !== null) {
    const ground = mixColors(theme.accent, WHITE, 0.82);
    const text = clipBody(page, tag, 700, 18, width - 60);
    ctx.font = bodyFont(page, 700, 18);
    const tagWidth = ctx.measureText(text).width + 52;
    fillRect(ctx, { x: RIGHT - tagWidth, y, w: tagWidth, h: 40 }, ground, Math.min(radius, 20));
    ctx.fillStyle = theme.accent;
    ctx.beginPath();
    ctx.arc(RIGHT - 18, y + 20, 5, 0, Math.PI * 2);
    ctx.fill();
    drawText(ctx, text, RIGHT - 32, y + 27, {
      font: bodyFont(page, 700, 18),
      color: readable(mixColors(theme.accent, '#000000', 0.45), ground, [ink], 4.5),
    });
    y += 64;
  }

  // Short strengths as chips along the opening's foot.
  const chips = flyer.benefits
    .map((benefit) => benefit.title)
    .filter((title) => title.length <= 22)
    .slice(0, 3);
  const chipsTop = heroBottom - 64;
  const textBottom = chips.length > 0 && heroBottom === 632 ? chipsTop - 24 : heroBottom - 40;

  const kicker = kickerOf(flyer);
  const kickerHeight = 40;
  const title = fit(page, flyer.title, 'display', {
    maxWidth: width,
    maxLines: 3,
    maxSize: 66,
    minSize: 40,
    weight: 800,
  });
  let titleHeight = title.lines.length * title.size * 1.12;
  const descriptionRoom = textBottom - y - titleHeight - 22 - kickerHeight;
  // Whole sentences only: a sentence cut in the middle is not drawn (fitDescription).
  const text =
    descriptionRoom < 40
      ? null
      : fitDescription(page, flyer.description, {
          maxWidth: width,
          maxLines: Math.max(1, Math.min(4, Math.floor(descriptionRoom / (23 * 1.6)))),
          maxSize: 23,
          minSize: 20,
          weight: 400,
        });
  titleHeight = drawLines(ctx, title, {
    x: RIGHT,
    y,
    font: displayFont(page, title.size),
    color: ink,
    lineHeight: 1.12,
  });
  y += titleHeight + 18;
  if (text !== null) {
    y += drawLines(ctx, text, {
      x: RIGHT,
      y,
      font: bodyFont(page, 400, text.size),
      color: muted,
      lineHeight: 1.6,
    });
    y += 10;
  }
  drawText(ctx, clipBody(page, kicker, 800, 19, width), RIGHT, y + 26, {
    font: bodyFont(page, 800, 19),
    color: brand,
    spacing: 1,
  });

  if (chips.length > 0 && heroBottom === 632) {
    let x = RIGHT;
    x -=
      drawText(ctx, 'בקצרה:', x, chipsTop + 27, { font: bodyFont(page, 500, 18), color: muted }) +
      14;
    for (const chip of chips) {
      ctx.font = bodyFont(page, 600, 17);
      const chipWidth = ctx.measureText(chip).width + 36;
      if (x - chipWidth < 560) break;
      fillRect(ctx, { x: x - chipWidth, y: chipsTop, w: chipWidth, h: 40 }, WHITE, 20);
      strokeRect(
        ctx,
        { x: x - chipWidth, y: chipsTop, w: chipWidth, h: 40 },
        mixColors(ink, WHITE, 0.85),
        1.5,
        20,
      );
      drawText(ctx, chip, x - chipWidth / 2, chipsTop + 27, {
        font: bodyFont(page, 600, 17),
        color: ink,
        align: 'center',
      });
      x -= chipWidth + 10;
    }
  }

  // The figures, on white cards.
  if (tiles.length > 0) {
    const gap = 18;
    const cardWidth = (RIGHT - LEFT - gap * 3) / 4;
    tiles.forEach((tile, index) => {
      const box = {
        x: RIGHT - (index + 1) * cardWidth - index * gap,
        y: 664,
        w: cardWidth,
        h: 106,
      };
      ctx.save();
      ctx.shadowColor = 'rgba(31, 28, 29, 0.06)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 4;
      fillRect(ctx, box, WHITE, Math.min(radius, 16));
      ctx.restore();
      strokeRect(ctx, box, mixColors(ink, WHITE, 0.88), 1.5, Math.min(radius, 16));
      const right = box.x + box.w - 22;
      if (tile.kind === 'FIGURE') {
        const { value, unit, label } = tile.figure;
        const unitWidth =
          unit === ''
            ? 0
            : drawText(ctx, unit, right, box.y + 56, {
                font: bodyFont(page, 700, 19),
                color: brand,
                direction: 'ltr',
              });
        const big = fit(page, value, 'display', {
          maxWidth: box.w - 44 - unitWidth,
          maxLines: 1,
          maxSize: 46,
          minSize: 24,
          weight: 800,
        });
        drawText(ctx, big.lines[0] ?? '', right - unitWidth - (unit === '' ? 0 : 3), box.y + 56, {
          font: displayFont(page, big.size),
          color: brand,
          direction: 'ltr',
        });
        drawText(ctx, clipBody(page, label, 500, 17, box.w - 44), right, box.y + 88, {
          font: bodyFont(page, 500, 17),
          color: muted,
        });
      } else {
        drawIcon(ctx, benefitIcon(tile.icon), right - 18, box.y + 34, 34, brand, 2.2);
        const caption = fit(page, tile.title, 'body', {
          maxWidth: box.w - 44,
          maxLines: 2,
          maxSize: 18,
          minSize: 15,
          weight: 700,
        });
        drawLines(ctx, caption, {
          x: right,
          y: box.y + 56,
          font: bodyFont(page, 700, caption.size),
          color: ink,
          lineHeight: 1.2,
        });
      }
    });
  }

  // The price bar, with the white button.
  const bar = { x: LEFT, y: 800, w: RIGHT - LEFT, h: 138 };
  fillRect(ctx, bar, theme.brand, radius);
  const onBrand = textColorOn(theme.brand);
  const soft = mixColors(onBrand, theme.brand, 0.25);
  const button = { x: bar.x + 32, y: bar.y + 39, w: 236, h: 60 };
  fillRect(ctx, button, WHITE, Math.min(radius, 14));
  const buttonColor = readable(theme.brand, WHITE, [ink], 4.5);
  drawIcon(
    ctx,
    flyer.store.whatsapp === null ? PHONE_ICON : WHATSAPP_ICON,
    button.x + button.w - 34,
    button.y + 30,
    26,
    buttonColor,
    2.2,
  );
  drawText(ctx, WORDS.cta, button.x + button.w - 56, button.y + 38, {
    font: bodyFont(page, 800, 21),
    color: buttonColor,
  });

  const { price } = flyer;
  const barRight = bar.x + bar.w - 30;
  if (price !== null) {
    drawText(ctx, saving === null ? WORDS.price : WORDS.salePrice, barRight, bar.y + 44, {
      font: bodyFont(page, 500, 18),
      color: soft,
    });
    const priceWidth = drawAmount(page, price.amountMinor, {
      x: barRight,
      baseline: bar.y + 112,
      size: 72,
      color: onBrand,
      maxWidth: 300,
    });
    if (saving !== null && price.compareAtMinor !== null) {
      const columns: { label: string; amount: number; struck: boolean }[] = [
        { label: WORDS.regular, amount: price.compareAtMinor, struck: true },
        { label: WORDS.saving, amount: saving, struck: false },
      ];
      let x = barRight - Math.max(priceWidth, 200) - 36;
      for (const column of columns) {
        if (x - 150 < button.x + button.w + 24) break;
        line(
          ctx,
          [x, bar.y + 30],
          [x, bar.y + bar.h - 30],
          mixColors(onBrand, theme.brand, 0.7),
          1.5,
        );
        const right = x - 32;
        drawText(ctx, column.label, right, bar.y + 52, {
          font: bodyFont(page, 500, 18),
          color: soft,
        });
        if (column.struck) {
          drawStruck(page, column.amount, {
            x: right,
            baseline: bar.y + 100,
            font: displayFont(page, 32),
            color: soft,
            strike: onBrand,
          });
        } else {
          drawAmount(page, column.amount, {
            x: right,
            baseline: bar.y + 102,
            size: 40,
            color: onBrand,
            maxWidth: 170,
          });
        }
        x = right - 150 - 30;
      }
    }
  } else {
    drawText(ctx, WORDS.quote, barRight, bar.y + 78, {
      font: displayFont(page, 44),
      color: onBrand,
      maxWidth: barRight - button.x - button.w - 30,
    });
    drawText(ctx, WORDS.noCommitment, barRight, bar.y + 112, {
      font: bodyFont(page, 500, 20),
      color: soft,
    });
  }

  // The footer.
  fillRect(ctx, { x: 0, y: 974, w: W, h: W - 974 }, ink);
  const onInk = textColorOn(ink);
  drawText(ctx, 'דברו איתנו', RIGHT, 1020, { font: displayFont(page, 30), color: onInk });
  const address =
    flyer.store.website === null ? flyer.store.name : flyerAddress(flyer.store.website);
  drawText(ctx, clipBody(page, address, 400, 18, 420), RIGHT, 1052, {
    font: bodyFont(page, 400, 18),
    color: mixColors(onInk, ink, 0.4),
    direction: flyer.store.website === null ? 'rtl' : 'ltr',
  });
  const phone = phoneOf(flyer);
  if (phone !== null) {
    drawIcon(ctx, PHONE_ICON, LEFT + 16, 1027, 30, readable(theme.accent, ink, [onInk]), 2.2);
    drawText(ctx, clipBody(page, phone, 800, 34, 420), LEFT + 46, 1040, {
      font: displayFont(page, 38),
      color: onInk,
      align: 'left',
      direction: 'ltr',
    });
  }
}
